import React, { useCallback, useEffect, useState } from "react";
import client, { formatApiError } from "@/lib/api";
import { Users, Plus, Trash2, ArrowLeft, Search, AlertCircle, TrendingUp, CalendarCheck, UserPlus } from "lucide-react";

const inputCls = "w-full px-3 py-2.5 rounded-xl border-2 border-[#E5DFC8] focus:outline-none focus:border-hv-teal bg-white text-sm";

const AVATAR_TONES = ["bg-hv-teal", "bg-hv-coral", "bg-[#B27E0A]", "bg-[#7A6FD1]", "bg-[#C9527A]"];
const avatarTone = (seed) => AVATAR_TONES[[...String(seed)].reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_TONES.length];

function MiniStat({ icon: Icon, value, label, testid }) {
  const Ico = Icon;
  return (
    <div className="rounded-xl bg-hv-cream p-3 text-center" data-testid={testid}>
      <Ico size={14} strokeWidth={2.5} className="text-hv-teal mx-auto" />
      <div className="hv-heading text-hv-navy text-lg mt-1">{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-hv-slate font-bold mt-0.5">{label}</div>
    </div>
  );
}

function AssignTeacher({ schoolId, onDone }) {
  const [email, setEmail] = useState("");
  const [className, setClassName] = useState("");
  const [sectionName, setSectionName] = useState("");
  const [maxLearners, setMaxLearners] = useState(30);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [found, setFound] = useState(null);

  const lookup = async () => {
    setErr(""); setFound(null);
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { data } = await client.get("/principal/teachers/lookup", { params: { email: email.trim() } });
      setFound(data.teacher);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally { setBusy(false); }
  };

  const createSection = async () => {
    if (!found || !className.trim() || !sectionName.trim()) return;
    setBusy(true); setErr("");
    try {
      await client.post("/admin/sections", {
        school_id: schoolId, name: sectionName.trim(), class_name: className.trim(),
        max_learners: Number(maxLearners) || 30, teacher_guardian_id: found.id,
      });
      setEmail(""); setClassName(""); setSectionName(""); setFound(null);
      onDone();
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="hv-card p-6" data-testid="assign-teacher">
      <div className="flex items-center gap-3">
        <div className="hv-icon-circle w-10 h-10 rounded-full flex items-center justify-center">
          <Plus size={18} strokeWidth={2.5} />
        </div>
        <span className="hv-badge">Assign an existing teacher to a new class</span>
      </div>
      <p className="text-hv-slate text-xs mt-2">
        She must already have a teacher account — a principal can assign, not create, a teacher.
      </p>
      <div className="mt-4 grid sm:grid-cols-[1fr_auto] gap-3">
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="teacher@email.com"
               data-testid="assign-teacher-email" className={inputCls} />
        <button onClick={lookup} disabled={busy} data-testid="assign-teacher-lookup" className="hv-btn-secondary inline-flex items-center gap-2">
          <Search size={16} strokeWidth={2.5} /> Find
        </button>
      </div>
      {found && (
        <div className="mt-4 rounded-2xl bg-[#E3F1EC] border border-hv-teal/40 p-4" data-testid="assign-teacher-found">
          <div className="hv-heading text-hv-navy text-sm">{found.name || found.email}</div>
          <div className="grid sm:grid-cols-3 gap-3 mt-3">
            <input value={className} onChange={(e) => setClassName(e.target.value)} placeholder="Class (e.g. Year 5)"
                   data-testid="assign-teacher-class" className={inputCls} />
            <input value={sectionName} onChange={(e) => setSectionName(e.target.value)} placeholder="Section (e.g. Blue)"
                   data-testid="assign-teacher-section" className={inputCls} />
            <input type="number" min={1} value={maxLearners} onChange={(e) => setMaxLearners(e.target.value)}
                   placeholder="Seats" data-testid="assign-teacher-seats" className={inputCls} />
          </div>
          <button onClick={createSection} disabled={busy} data-testid="assign-teacher-create"
                  className="hv-btn-primary mt-3">
            {busy ? "Creating…" : "Create class & assign"}
          </button>
        </div>
      )}
      {err && <p className="text-hv-coral text-sm mt-3 font-bold">{err}</p>}
    </div>
  );
}

function TeacherRoster({ teacher, cls, schoolId, onBack }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    setErr("");
    // Principal-only read: /teacher/section-summary is teacher/admin-only
    // and would 403 for a principal caller, so she has her own read-only
    // route to the same roster/stats shape.
    client.get("/principal/section-summary", {
      params: { scope_type: cls.scope_type, scope_id: cls.scope_id, ...(schoolId ? { school_id: schoolId } : {}) },
    })
      .then(({ data }) => setData(data))
      .catch((ex) => setErr(formatApiError(ex.response?.data?.detail) || ex.message));
  }, [cls, schoolId]);

  return (
    <div data-testid="teacher-roster-drilldown">
      <button onClick={onBack} className="text-hv-teal font-bold text-sm inline-flex items-center gap-1">
        <ArrowLeft size={16} /> Back to teachers
      </button>
      <h3 className="hv-heading mt-3 text-xl text-hv-navy">{teacher.name || teacher.email} — {cls.name}</h3>
      {err && <p className="mt-4 text-hv-coral text-sm font-bold" data-testid="teacher-roster-error">{err}</p>}
      {!data && !err && <div className="mt-4 text-hv-slate">Loading…</div>}
      {data && (
        <div className="mt-4 space-y-2">
          {data.roster.map((s) => (
            <div key={s.id} className="rounded-2xl border-2 border-[#E5DFC8] p-3 flex items-center justify-between text-sm">
              <span className="font-bold text-hv-navy">{s.name}</span>
              <span className="text-hv-slate">{s.completed}/{s.unlocked} complete</span>
            </div>
          ))}
          {!data.roster.length && <p className="text-hv-slate text-sm">No students in this class yet.</p>}
        </div>
      )}
    </div>
  );
}

export default function PrincipalTeachers() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [drill, setDrill] = useState(null); // {teacher, cls}
  const [schoolId, setSchoolId] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: overview } = await client.get("/principal/overview");
      setSchoolId(overview.school?.id || null);
      const { data } = await client.get("/principal/teachers", { params: overview.school?.id ? { school_id: overview.school.id } : {} });
      setData(data);
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const removeTeacher = async (teacherId, name) => {
    if (!window.confirm(`Remove ${name}? This deletes her classes and every one of her students.`)) return;
    try {
      await client.delete(`/principal/teachers/${teacherId}`, { params: schoolId ? { school_id: schoolId } : {} });
      setMsg(`Removed ${name}.`);
      load();
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || ex.message);
    }
  };

  if (drill) return <TeacherRoster teacher={drill.teacher} cls={drill.cls} schoolId={schoolId} onBack={() => setDrill(null)} />;

  return (
    <div data-testid="principal-teachers">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="hv-heading text-2xl text-hv-navy">My Teachers</h2>
          <p className="text-hv-slate text-sm mt-1">Every teacher in your school, their classes, and their students.</p>
        </div>
        {schoolId && (
          <details className="group" data-testid="assign-teacher-toggle">
            <summary className="hv-btn-primary inline-flex items-center gap-2 cursor-pointer list-none">
              <UserPlus size={18} strokeWidth={2.5} /> Assign Teacher
            </summary>
            <div className="mt-4"><AssignTeacher schoolId={schoolId} onDone={load} /></div>
          </details>
        )}
      </div>

      {err && <p className="text-hv-coral text-sm mt-3 font-bold">{err}</p>}
      {msg && <p className="text-hv-teal text-sm mt-3 font-bold">{msg}</p>}

      <div className="mt-6 grid md:grid-cols-2 gap-4">
        {(data?.teachers || []).map((t) => {
          const label = t.teacher.name || t.teacher.email;
          const attendance = t.attendance_today_pct === null || t.attendance_today_pct === undefined ? "—" : `${t.attendance_today_pct}%`;
          return (
            <div key={t.teacher.id} className="hv-card p-6" data-testid={`teacher-card-${t.teacher.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 font-bold text-white text-lg ${avatarTone(label)}`}>
                    {label.slice(0, 1).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <div className="hv-heading text-hv-navy text-base truncate">{label}</div>
                    <div className="text-hv-slate text-xs truncate">{t.teacher.email}</div>
                  </div>
                </div>
                <button onClick={() => removeTeacher(t.teacher.id, label)}
                        data-testid={`remove-teacher-${t.teacher.id}`}
                        className="text-hv-slate hover:text-hv-coral shrink-0" title="Remove teacher">
                  <Trash2 size={18} strokeWidth={2.5} />
                </button>
              </div>

              {t.classes.length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {t.classes.map((c) => (
                    <button key={c.scope_id} onClick={() => setDrill({ teacher: t.teacher, cls: c })}
                            data-testid={`teacher-class-${c.scope_id}`}
                            className="hv-badge hover:bg-hv-teal-tint">
                      {c.class_name ? `${c.class_name} · ${c.name}` : c.name}
                    </button>
                  ))}
                </div>
              )}

              <div className="mt-4 grid grid-cols-3 gap-2">
                <MiniStat testid={`teacher-stat-students-${t.teacher.id}`} icon={Users} value={t.student_count} label="Students" />
                <MiniStat testid={`teacher-stat-progress-${t.teacher.id}`} icon={TrendingUp} value={`${t.avg_progress_pct}%`} label="Progress" />
                <MiniStat testid={`teacher-stat-attendance-${t.teacher.id}`} icon={CalendarCheck} value={attendance} label="Attendance" />
              </div>

              <div className="mt-4 flex items-center gap-1.5 text-xs font-bold text-hv-teal">
                <span className="w-2 h-2 rounded-full bg-hv-teal inline-block" /> Active
              </div>
            </div>
          );
        })}
        {data && !data.teachers.length && (
          <div className="hv-card p-7 text-hv-slate inline-flex items-center gap-2 col-span-2">
            <AlertCircle size={18} /> No teachers assigned yet.
          </div>
        )}
        {!data && <div className="p-6 text-hv-slate">Loading…</div>}
      </div>
    </div>
  );
}
